import type { ChangeEvent, ReactNode } from "react";
import "./styles/Form.css";

interface FieldShellProps {
  id: string;
  label: string;
  error?: string;
  hint?: string;
  isOptional?: boolean;
  children: ReactNode;
}

function FieldShell({ id, label, error, hint, isOptional, children }: FieldShellProps) {
  return (
    <div className={error ? "form-field form-field--error" : "form-field"}>
      <label htmlFor={id}>
        {label}
        {isOptional && <span className="form-field__optional"> (optional)</span>}
      </label>
      {children}
      {hint && !error && <p id={`${id}-hint`} className="form-field__hint">{hint}</p>}
      {error && <p id={`${id}-error`} className="form-field__error" role="alert">{error}</p>}
    </div>
  );
}

function describedBy(id: string, error?: string, hint?: string): string | undefined {
  if (error) return `${id}-error`;
  return hint ? `${id}-hint` : undefined;
}

interface TextFieldProps {
  id: string;
  name: string;
  label: string;
  value: string;
  onChange: (name: string, value: string) => void;
  type?: "text" | "email";
  autoComplete?: string;
  maxLength?: number;
  error?: string;
  hint?: string;
  isOptional?: boolean;
}

export function TextField({ id, name, label, value, onChange, type = "text", autoComplete, maxLength, error, hint, isOptional }: TextFieldProps) {
  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    onChange(name, event.target.value);
  }

  return (
    <FieldShell id={id} label={label} error={error} hint={hint} isOptional={isOptional}>
      <input
        id={id}
        name={name}
        type={type}
        value={value}
        autoComplete={autoComplete}
        maxLength={maxLength}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, error, hint)}
        onChange={handleChange}
      />
    </FieldShell>
  );
}

interface TextAreaFieldProps {
  id: string;
  name: string;
  label: string;
  value: string;
  onChange: (name: string, value: string) => void;
  rows?: number;
  maxLength?: number;
  error?: string;
  hint?: string;
  isOptional?: boolean;
}

export function TextAreaField({ id, name, label, value, onChange, rows = 4, maxLength, error, hint, isOptional }: TextAreaFieldProps) {
  function handleChange(event: ChangeEvent<HTMLTextAreaElement>) {
    onChange(name, event.target.value);
  }

  return (
    <FieldShell id={id} label={label} error={error} hint={hint} isOptional={isOptional}>
      <textarea
        id={id}
        name={name}
        value={value}
        rows={rows}
        maxLength={maxLength}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, error, hint)}
        onChange={handleChange}
      />
    </FieldShell>
  );
}

interface CheckboxFieldProps {
  id: string;
  name: string;
  checked: boolean;
  onChange: (name: string, checked: boolean) => void;
  error?: string;
  children: ReactNode;
}

export function CheckboxField({ id, name, checked, onChange, error, children }: CheckboxFieldProps) {
  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    onChange(name, event.target.checked);
  }

  return (
    <div className={error ? "form-checkbox form-checkbox--error" : "form-checkbox"}>
      <input
        id={id}
        name={name}
        type="checkbox"
        checked={checked}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        onChange={handleChange}
      />
      <label htmlFor={id}>{children}</label>
      {error && <p id={`${id}-error`} className="form-field__error" role="alert">{error}</p>}
    </div>
  );
}

interface HoneypotFieldProps {
  value: string;
  onChange: (name: string, value: string) => void;
}

// A field real visitors never see. Bots that fill in every input give themselves away by filling this one.
export function HoneypotField({ value, onChange }: HoneypotFieldProps) {
  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    onChange("website", event.target.value);
  }

  return (
    <div className="form-trap" aria-hidden="true">
      <label htmlFor="website">Leave this field empty</label>
      <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" value={value} onChange={handleChange} />
    </div>
  );
}
